import { Component } from '@angular/core';
import { OnInit, OnDestroy, Input } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { ViewportService } from '../../services/viewport.service';
import { UserService } from '../../services/user.service';
import { ConfigService } from '../../services/config.service';
import { SearchService } from '../../search/services/search.service';
import { formatString } from '../../utils/string-utils';
import { DocViewerFactoryService } from '../../services/factory/doc-viewer-factory.service';
import { RoutingService} from '../../services/routing.service';
import cloneDeep from 'lodash/cloneDeep';

/**
 * App dashboard component.
 */
@Component({
	selector: 'kv-dashboard',
	templateUrl: './dashboard.component.html',
	styleUrl: './dashboard.component.css'
})
export class DashboardComponent implements OnInit, OnDestroy {
	@Input() userView: string = 'G';

	private userEventSubscription!: Subscription;
	private searchEventSubscription!: Subscription;

	LOCALIZATION: any;

	isMobile: boolean = false;
	thumbnailURL: string = '';
	dashboard: any = {};

	constructor(
		private ngbModal: NgbModal,
		private viewportService: ViewportService,
		private configService: ConfigService,
		private userService: UserService,
		private searchService: SearchService,
		private routingService: RoutingService,
		private docViewerFactory: DocViewerFactoryService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;

		// Replacement for the viewportsizemonitor directive
		this.viewportService.isMobile$.subscribe(isMobile => {
			this.isMobile = isMobile;
		});
	}

	ngOnInit(): void {
		this.userEventSubscription = this.userService.broadcastEvent.subscribe((event: string) => {
			if (event === 'localizationLoaded') {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			}
			else if (event === "userPrefsUpdate") {
				this.processDashboard();
			}
		});

		this.searchEventSubscription = this.searchService.broadcastEvent.subscribe(event => {
			if (event.type === 'searchEndedDashboard') {
				this.listenToSearchEndedDashboardEvent(event.data);
			}
		});

		this.thumbnailURL = this.configService.getConfig().record.thumbnailURL;

		this.processDashboard();
	}

	ngOnDestroy() {
		if (this.userEventSubscription) {
			this.userEventSubscription.unsubscribe();
		}

		if (this.searchEventSubscription) {
			this.searchEventSubscription.unsubscribe();
		}
	}

	processDashboard(): void {
		let property = this.userService.user.properties.awpropertyObjectList.find((x: any) => { return x.name == "DASHBOARD_ID"; });

		if (!property || !property.value) {
			return;
		}

		let id = property.value;
		this.userService.getDashboard(id).then(
			(data: any) => {
				let dashboard = data.config;
				dashboard.id = id;

				let contentBlocks = dashboard.contentBlocks || [];
				for (let i = 0; i < contentBlocks.length; i++) {
					let block = contentBlocks[i];

					if (block.action == 'searchBox') {
						let search = block.search;
						if (search && search.maxRecords > 0) {
							let params = {
								query: search.dataQuery,
								pageIndex: 0,
								pageSize: undefined,
								libraries: search.libraries.split(","),
								dataQueryType: search.dataQueryType,
								dashboardIndex: i,
								resultView: this.userView
							};
							this.searchService.doSearchDashboard(params);
						}
					}
				}
				this.dashboard = dashboard;
			},
			(err: any) => {
				console.error("Failed to fetch user dashboard.", err);
				this.dashboard = {};
			}
		);
	}

	dashboardBlockClick(block: any): void {
		try {
			if (block.search && block.action.toLowerCase() === 'searchbox') {
				this.dashboardSearch(block);
			} else if (block.browse && block.action.toLowerCase() === 'browsebox') {
				this.dashboardBrowse(block);
			} else if (block.link && block.link.URL) {
				if (block.link.openNewTab && block.link.openNewTab.toLowerCase() === "false") {
					location.href = block.link.URL;
				} else {
					window.open(block.link.URL, '_blank');
				}
			}
		} catch (err) {
			console.error("Dashboard click failed.", err);
		}
	}

	dashboardBrowse(block: any): void {
		try {
			if (block.browse) {
				this.routingService.updateSearchUrl({
					a: 'b',
					v: this.userView,
					browseconfig: JSON.stringify(block)
				}, true, true);
			}
		} catch (err) {
			console.error("Dashboard browse failed.", err);
		}
	}

	dashboardSearch(block: any): void {
		try {
			if (block.search) {
				block.libraries = block.search.libraries;
				block.view = this.userView;
				this.searchService.dashboardSearch(block);
			}
		} catch (err) {
			console.error("Dashboard search failed.", err);
		}
	}

	scrollDashboard(index: number, left: boolean): void {
		let blockIndex = this.dashboard.contentBlocks[index].blockIndex;
		this.dashboard.contentBlocks[index].blockIndex = left ? blockIndex - 1 : blockIndex + 1;
	}

	openDashboardResult(results: any, id: string): void {
		// Why do this?
		if (this.searchService.results) {
			this.searchService.results.total = results.length;
			this.searchService.results.records = results;
		}

		const viewerComponent = this.docViewerFactory.getViewerComponent();
		const modalRef = this.ngbModal.open(viewerComponent, {
			windowClass: "viewer-modal-window"
		});
		
		modalRef.componentInstance.productId = id;

		modalRef.result.then(
		    (payload) => {
		        console.log('Modal dismissed');
		    },
		    (reason) => {
		        console.log('Modal dismissed');
		    }
		);
	}

	private listenToSearchEndedDashboardEvent(args: any) {
		let results = (args.records !== null && Array.isArray(args.records)) ? cloneDeep(args.records) : [];
		if (args.index !== undefined && args.index >= 0) {
			if (this.dashboard.contentBlocks[args.index].search &&
				this.dashboard.contentBlocks[args.index].search.showThumbnail.toLowerCase() === "true") {
				for (let i = 0; i < results.length; i++) {
					let record = results[i];
					results[i].thumbnail = formatString(this.thumbnailURL, record.uuid);
				}
			}
			this.dashboard.contentBlocks[args.index].blockIndex = 0;
			this.dashboard.contentBlocks[args.index].results = results;
		}
	}
}
